import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { useConfig } from "../../../config/ConfigContext";
import { useNavigationContext } from "../../common/navigation/NavigationContext";
import {
  getRepresentativeProfile,
  saveRepresentativeProfile,
  registerCompanyFull,
  registerChildCompanyFull,
} from "./representativeInteractionService";
import { validateAddress } from "../../common/dogma/dogmaInteractionService";
import {
  searchCompanies,
  getCompanyProfile,
  mapRegistryAddress,
  REGISTRY_SUPPORTED_COUNTRIES,
} from "../../common/registry/registryInteractionService";
// 🎯 Alias bypasses the underscore naming rules for react/jsx-pascal-case
import RepresentativeView from "./SEC_RepresentativeView";

// Collapses repeated whitespace and trims - applied to optional address
// fields whenever they're supplied manually (Line 2, Town, County).
const cleanAddressField = (value) => (value || "").replace(/\s+/g, " ").trim();

// ISO2 codes - must match seeded rows in Ref.Z_02_06_Country_Master
const VALID_COUNTRIES = ["GB", "FR", "CH", "DE"];

/**
 * 🎯 Self-service registration only supports CREATING a new company. Attaching
 * to an EXISTING company is invitation-only (an existing Representative/Admin
 * invites a user in) — not built here. Never expose the company registry for
 * browsing/searching in this flow; that would leak other companies' existence
 * to any registered user, a privacy breach.
 */
export default function SEC_RepresentativeController() {
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const { messages } = useConfig();

  // 🎯 CHILD COMPANY MODE: reached via "Manage Child Companies" on the Company
  // Dashboard as /registered/represent-company?parentId=<id>. Identical flow to
  // self-registering a new company, except the create call is create-child-company-full
  // (ParentEntityId set, no email-alignment check, no Representative grant — the
  // caller already has access via the parent's hierarchy scope).
  const location = useLocation();
  const parentEntityId = new URLSearchParams(location.search).get("parentId");
  const isChildMode = !!parentEntityId;

  const { setWorkflowContext } = useNavigationContext();
  const [step, setStep] = useState("LOADING"); // LOADING | PROFILE | COMPANY | ATTESTATION | DONE
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setWorkflowContext(
      isChildMode ? "Register Child Company" : "Register Company",
      "/registered/represent-company"
    );
  }, [setWorkflowContext, isChildMode]);

  // Step 1: identity
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  // Step 2: create new company - captured in one hit (identity, address, reg number)
  const [newCompany, setNewCompany] = useState({
    name: "",
    regNumber: "",
    country: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    county: "",
    postcode: "",
  });
  const [corporateEntityId, setCorporateEntityId] = useState(null);
  const [companyName, setCompanyName] = useState("");

  // Address lookup is folded into Create & Represent itself, not a separate
  // step/button - one action, quietly backed by a validate-then-fallback
  // attempt. Once validated for a given Line1+Postcode+Country, we don't
  // re-run it on a retry submit unless one of those three changed.
  const [addressValidated, setAddressValidated] = useState(false);

  // Company registry check (Companies House / DINUM / Zefix) - separate
  // from address validation. This looks the COMPANY up by name and/or reg
  // number+country; if confirmed, its registered address populates the
  // form directly and is treated as already-authoritative (addressValidated
  // is set true, so Create & Represent's own Dogma/Google lookup is skipped
  // entirely for a confirmed match). If no match is confirmed, the form
  // falls back unchanged to the existing manual-entry + Dogma-on-submit path.
  // idle | checking | results | confirmed | rejected
  const [registryStatus, setRegistryStatus] = useState("idle");
  const [registryResults, setRegistryResults] = useState([]);
  const [registryError, setRegistryError] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [confirmedCompany, setConfirmedCompany] = useState(null);

  useEffect(() => {
    const init = async () => {
      const profile = await getRepresentativeProfile({ siteKey });
      if (profile) {
        setFullName(GoldenSourceHelper.get(profile, "FullName") || "");
        setDateOfBirth((GoldenSourceHelper.get(profile, "DateOfBirth") || "").slice(0, 10));
        setStep("COMPANY");
      } else {
        setStep("PROFILE");
      }
    };
    init();
  }, [siteKey]);

  const handleSaveProfile = useCallback(async () => {
    if (!fullName || !dateOfBirth) {
      setError("Full name and date of birth are required.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await saveRepresentativeProfile(fullName, dateOfBirth, { siteKey });
      setStep("COMPANY");
    } catch (err) {
      setError(err.message || "Could not save profile.");
    } finally {
      setSubmitting(false);
    }
  }, [fullName, dateOfBirth, siteKey]);

  // Editing the primary lookup key invalidates a prior silent validation -
  // a retry submit will attempt the lookup fresh rather than reuse stale data.
  // Also invalidates any confirmed/pending registry check - country changes
  // which registry (or none) applies.
  const handlePrimaryFieldChange = useCallback((field, value) => {
    setNewCompany((p) => ({ ...p, [field]: value }));
    setAddressValidated(false);
    setRegistryStatus("idle");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
    setConfirmedCompany(null);
  }, []);

  // Name/Reg Number are the registry lookup's own inputs - editing either
  // after a confirmed match invalidates it (the confirmed address no longer
  // corresponds to what's typed), same reasoning as handlePrimaryFieldChange.
  const handleCompanyFieldChange = useCallback((field, value) => {
    setNewCompany((p) => ({ ...p, [field]: value }));
    setRegistryStatus("idle");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
    setConfirmedCompany(null);
  }, []);

  // Single check: if a Reg Number is given, look the company up directly by
  // ID (most precise). Otherwise fall back to a name search. Unsupported
  // countries (DE currently has no working registry adapter) are skipped
  // silently - straight to manual/Dogma entry, no error shown for that case.
  const handleCheckRegistration = useCallback(async () => {
    const { name, regNumber, country } = newCompany;
    if (!country || !REGISTRY_SUPPORTED_COUNTRIES.includes(country)) {
      setRegistryStatus("rejected");
      return;
    }
    if (!name && !regNumber) {
      setRegistryError("Enter a Company Name or Reg Number first.");
      return;
    }

    setRegistryError("");
    setRegistryStatus("checking");
    setSelectedMatchId(null);
    try {
      if (regNumber) {
        const profile = await getCompanyProfile(regNumber, country, { siteKey });
        if (profile) {
          setRegistryResults([profile]);
          setRegistryStatus("results");
          return;
        }
      }
      if (name) {
        const results = await searchCompanies(name, country, { siteKey });
        if (results.length > 0) {
          setRegistryResults(results);
          setRegistryStatus("results");
          return;
        }
      }
      setRegistryStatus("rejected");
    } catch (err) {
      setRegistryError(err.message || "Company registry check failed.");
      setRegistryStatus("idle");
    }
  }, [newCompany, siteKey]);

  // Radio-select a row in the results table - doesn't confirm anything by
  // itself, just marks which one "Confirm Selection" will act on.
  const handleSelectMatch = useCallback((companyId) => {
    setSelectedMatchId(companyId);
  }, []);

  // A search result only carries summary fields - resolve the full profile
  // (for its address) before populating, unless it's already a full profile
  // (the direct Reg-Number lookup path already has everything it needs).
  const handleConfirmSelection = useCallback(async () => {
    const match = registryResults.find((r) => String(r.companyId) === String(selectedMatchId));
    if (!match) {
      setRegistryError("Select a company from the list first.");
      return;
    }

    setRegistryError("");
    try {
      const profile = match.registeredAddress
        ? match
        : await getCompanyProfile(match.companyId, newCompany.country, { siteKey });

      if (!profile) {
        setRegistryError("Could not retrieve full company details - try again or proceed manually.");
        return;
      }

      const address = mapRegistryAddress(profile);
      const resolvedId = String(profile.companyId ?? match.companyId ?? "");
      setNewCompany((p) => ({
        ...p,
        name: profile.companyName || p.name,
        regNumber: resolvedId || p.regNumber,
        ...address,
      }));
      setConfirmedCompany({ companyName: profile.companyName, companyId: resolvedId, ...address });
      setAddressValidated(true); // government-sourced - no Dogma re-validation needed
      setRegistryStatus("confirmed");
    } catch (err) {
      setRegistryError(err.message || "Could not retrieve full company details.");
    }
  }, [registryResults, selectedMatchId, newCompany.country, siteKey]);

  // "None of these" / no match found - proceed with the existing manual
  // address entry + Dogma-on-submit + manual-fallback flow, unchanged.
  const handleRejectRegistryMatches = useCallback(() => {
    setRegistryStatus("rejected");
    setRegistryResults([]);
    setRegistryError("");
    setSelectedMatchId(null);
  }, []);

  // Undo a confirmed match and return to the search step. Clears only the
  // registry-sourced address fields (they belonged to the discarded company)
  // - Name/Reg Number are left as typed so the user isn't forced to retype
  // before searching again.
  const handleDiscardConfirmedMatch = useCallback(() => {
    setNewCompany((p) => ({
      ...p,
      addressLine1: "",
      addressLine2: "",
      town: "",
      county: "",
      postcode: "",
    }));
    setAddressValidated(false);
    setConfirmedCompany(null);
    setRegistryResults([]);
    setSelectedMatchId(null);
    setRegistryError("");
    setRegistryStatus("idle");
  }, []);

  // Single action: Create & Represent. Address validation (Postcode + Line 1,
  // the compulsory primary key) is attempted quietly first - on success it
  // fills Town/County/Line 2 from Dogma; on failure it falls back to whatever
  // the user typed manually, with no separate error step shown. Company
  // Name + Reg ID are then checked server-side (the alignment check) as part
  // of the same submit - one button, one outcome, not a multi-step wizard.
  const handleCreateAndRepresent = useCallback(async () => {
    let working = {
      ...newCompany,
      addressLine2: cleanAddressField(newCompany.addressLine2),
      town: cleanAddressField(newCompany.town),
      county: cleanAddressField(newCompany.county),
    };

    const { name, regNumber, country, addressLine1, postcode } = working;
    if (!name || !regNumber || !country || !addressLine1 || !postcode) {
      setError("Company Name, Reg Number, Country, Address Line 1 and Postcode are all required.");
      return;
    }

    setError("");
    setSubmitting(true);

    // Provenance for future reference (Corp.Z_01_CorporateEntity.RegIdSourceCode /
    // ADCO.Z_02_01_Address_Master.AddressSourceCode) - the 3-case model:
    //   1. Registry confirmed  -> REGISTRY_CONFIRMED / REGISTRY (Dogma skipped below)
    //   2. Manual + Google hit -> SELF_DECLARED / GOOGLE_VALIDATED
    //   3. Manual, Google miss -> SELF_DECLARED / MANUAL_UNVERIFIED
    const regIdSourceCode = registryStatus === "confirmed" ? "REGISTRY_CONFIRMED" : "SELF_DECLARED";
    let addressSourceCode = registryStatus === "confirmed" ? "REGISTRY" : "MANUAL_UNVERIFIED";

    if (!addressValidated) {
      try {
        const found = await validateAddress([addressLine1, postcode], country, { siteKey });
        working = {
          ...working,
          addressLine1: found.addressLine1 || working.addressLine1,
          addressLine2: found.addressLine2 || working.addressLine2,
          town: found.town || working.town,
          county: found.county || working.county,
          postcode: found.postcode || working.postcode,
        };
        setAddressValidated(true);
        addressSourceCode = "GOOGLE_VALIDATED";
      } catch (err) {
        // Silent fallback - proceed with whatever was typed manually.
        // addressSourceCode stays MANUAL_UNVERIFIED (case 3).
        console.error("Address lookup failed, proceeding with manual entry:", err.message);
      }
    }

    try {
      const created = isChildMode
        ? await registerChildCompanyFull(
            { ...working, regIdSourceCode, addressSourceCode },
            Number(parentEntityId),
            { siteKey }
          )
        : await registerCompanyFull(
            { ...working, regIdSourceCode, addressSourceCode },
            { siteKey }
          );
      const newId = GoldenSourceHelper.get(created, "CorporateEntityId");
      setNewCompany(working);
      setCorporateEntityId(newId);
      setCompanyName(name);
      setStep("ATTESTATION");
    } catch (err) {
      // Server-side alignment check failure lands here - message is
      // deliberately generic, not a per-field validation error. Text comes
      // from public/site-config.json (messages.COMPANY_REGISTRATION_REJECTED)
      // so support/product can reword it without a redeploy - err.message is
      // only a last-resort fallback (e.g. if the config itself failed to load).
      setError(messages?.COMPANY_REGISTRATION_REJECTED || err.message || "Could not register company.");
    } finally {
      setSubmitting(false);
    }
  }, [newCompany, addressValidated, registryStatus, siteKey, messages?.COMPANY_REGISTRATION_REJECTED, isChildMode, parentEntityId]);

  return (
    <RepresentativeView
      step={step}
      error={error}
      submitting={submitting}
      fullName={fullName}
      setFullName={setFullName}
      dateOfBirth={dateOfBirth}
      setDateOfBirth={setDateOfBirth}
      onSaveProfile={handleSaveProfile}
      newCompany={newCompany}
      setNewCompany={setNewCompany}
      onPrimaryFieldChange={handlePrimaryFieldChange}
      onCompanyFieldChange={handleCompanyFieldChange}
      validCountries={VALID_COUNTRIES}
      registryStatus={registryStatus}
      registryResults={registryResults}
      registryError={registryError}
      selectedMatchId={selectedMatchId}
      confirmedCompany={confirmedCompany}
      onCheckRegistration={handleCheckRegistration}
      onSelectMatch={handleSelectMatch}
      onConfirmSelection={handleConfirmSelection}
      onRejectRegistryMatches={handleRejectRegistryMatches}
      onDiscardConfirmedMatch={handleDiscardConfirmedMatch}
      onCreateAndRepresent={handleCreateAndRepresent}
      corporateEntityId={corporateEntityId}
      companyName={companyName}
      onAttestationComplete={() => setStep("DONE")}
      backPath={isChildMode ? "/registered/company-dashboard" : "/registered/dashboard"}
    />
  );
}
